import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SUBSCRIPTION_SOURCE_LABELS, SUBSCRIPTION_STATUSES, SUBSCRIPTION_STATUS_LABELS, type BulkSubscribeResult, type CampaignAudience, type Paginated, type Subscription } from '@kontor/shared';
import { api, queryString } from '../../api';
import { ContactPicker, type PickedContact } from '../../components/ContactPicker';
import { ExpandRow, Summary } from '../../components/ExpandRow';
import { FilterBar, type FilterField } from '../../components/filter/FilterBar';
import { useSession } from '../../session';
import { RelationPicker } from '../contacts/relationTypes';
import { SubscriptionBadge, day, errorText, when } from './shared';
import './marketing.css';

/**
 * Abonnenten des Newsletters (KON-170). Ein Kontakt bekommt Kampagnen erst, wenn seine Einwilligung nachweisbar ist: Er bestätigt per Link in einer Mail (Double-Opt-In), oder
 * Sie halten den Nachweis fest, zum Beispiel eine mündliche Zusage. Abmeldungen bleiben als Sperre stehen.
 */
export function SubscribersPage() {
  const { can } = useSession();
  const client = useQueryClient();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const status = params.get('status') ?? '';
  const page = Number(params.get('page') ?? 1);
  const [adding, setAdding] = useState(false);
  const [bulk, setBulk] = useState(false);
  const [bulkMode, setBulkMode] = useState<'invite' | 'confirmed'>('invite');
  const [bulkNote, setBulkNote] = useState('');
  const [bulkAudience, setBulkAudience] = useState<CampaignAudience>({ relationTypeIds: [], kinds: [], postalCodePrefix: undefined });
  const [bulkAck, setBulkAck] = useState(false);
  const [contact, setContact] = useState<PickedContact | null>(null);
  const [mode, setMode] = useState<'invite' | 'confirmed'>('invite');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['marketing-subscriptions', { q, status, page }],
    queryFn: () => api<Paginated<Subscription>>('GET', `/marketing/subscriptions${queryString({ q, status, page, perPage: 25 })}`),
    placeholderData: keepPreviousData,
  });
  const refresh = () => {
    for (const key of ['marketing-subscriptions', 'marketing-overview']) client.invalidateQueries({ queryKey: [key] });
  };
  const update = (next: Record<string, string>) => {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) (v ? merged.set(k, v) : merged.delete(k));
    if (!('page' in next)) merged.delete('page');
    setParams(merged);
  };
  const fields: FilterField[] = [{ key: 'status', label: 'Stand', type: 'select', options: [{ value: '', label: 'Alle' }, ...SUBSCRIPTION_STATUSES.map((s) => ({ value: s, label: SUBSCRIPTION_STATUS_LABELS[s] }))] }];

  const add = useMutation({
    mutationFn: () => {
      if (!contact) throw new Error('Bitte wählen Sie einen Kontakt.');
      return api<{ data: Subscription }>('POST', '/marketing/subscriptions', { contactId: contact.id, mode, consentNote: mode === 'confirmed' ? note.trim() : undefined });
    },
    onSuccess: (r) => {
      refresh();
      setNotice(r.data.status === 'pending' ? `Die Bestätigungsmail ging an ${r.data.email}. Erst nach dem Klick auf den Link gilt die Anmeldung.` : `${r.data.contactName} ist angemeldet.`);
      setAdding(false);
      setContact(null);
      setNote('');
      setError(null);
    },
    onError: (e) => setError(e instanceof Error && !('status' in e) ? e.message : errorText(e, 'Das Anlegen hat nicht geklappt.')),
  });
  const bulkAudienceBody = { ...bulkAudience, postalCodePrefix: bulkAudience.postalCodePrefix || undefined };
  const bulkCount = useQuery({
    queryKey: ['marketing-bulk-count', JSON.stringify(bulkAudienceBody)],
    queryFn: async () => (await api<{ data: { count: number } }>('POST', '/marketing/subscriptions/bulk/preview', bulkAudienceBody)).data.count,
    enabled: bulk,
    placeholderData: keepPreviousData,
  });
  const bulkAdd = useMutation({
    mutationFn: async () => (await api<{ data: BulkSubscribeResult }>('POST', '/marketing/subscriptions/bulk', { mode: bulkMode, consentNote: bulkMode === 'confirmed' ? bulkNote.trim() : undefined, audience: bulkAudienceBody, acknowledged: bulkAck })).data,
    onSuccess: (r) => {
      refresh();
      client.invalidateQueries({ queryKey: ['marketing-bulk-count'] });
      setNotice(r.mailsQueued > 0 ? `${r.added} Kontakte aufgenommen. Die Bestätigungsmails gehen jetzt nach und nach raus. Wer nicht bestätigt, bekommt keinen Newsletter.` : `${r.added} Kontakte als angemeldet aufgenommen.`);
      setBulk(false);
      setBulkAck(false);
      setBulkNote('');
      setError(null);
    },
    onError: (e) => setError(errorText(e, 'Die Aufnahme hat nicht geklappt.')),
  });
  const toggleKind = (kind: 'person' | 'company') => setBulkAudience((a) => ({ ...a, kinds: a.kinds.includes(kind) ? a.kinds.filter((k) => k !== kind) : [...a.kinds, kind] }));
  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'resend' | 'unsubscribe' | 'remove' }) =>
      action === 'remove' ? api('DELETE', `/marketing/subscriptions/${id}`) : api('POST', `/marketing/subscriptions/${id}/${action}`, action === 'unsubscribe' ? {} : undefined),
    onSuccess: (_r, v) => {
      refresh();
      setError(null);
      setNotice(v.action === 'resend' ? 'Die Bestätigungsmail wurde erneut gesendet.' : null);
    },
    onError: (e) => setError(errorText(e, 'Das hat nicht geklappt.')),
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    add.mutate();
  };

  return (
    <>
      <div className="page-head">
        <h1>Abonnenten</h1>
        <span className="muted">{list.data ? `${list.data.meta.total} Einträge` : ''}</span>
        <Link to="/marketing" className="btn btn--secondary">Kampagnen</Link>
        {can('marketing.send') && <button type="button" className="btn btn--secondary" aria-expanded={bulk} onClick={() => { setBulk(!bulk); setAdding(false); setError(null); setNotice(null); }}>Alle Kontakte aufnehmen</button>}
        {can('marketing.write') && <button type="button" className="btn btn--primary" aria-expanded={adding} onClick={() => { setAdding(!adding); setBulk(false); setError(null); }}>Abonnent hinzufügen</button>}
      </div>
      {notice && <div className="alert alert--ok" role="status">{notice}</div>}
      {error && !adding && <div className="alert alert--error" role="alert">{error}</div>}
      {bulk && (
        <form className="card mk-form" aria-label="Alle Kontakte aufnehmen" onSubmit={(e) => { e.preventDefault(); setError(null); bulkAdd.mutate(); }}>
          <h2>Alle Kontakte aufnehmen</h2>
          {error && <div className="alert alert--error" role="alert">{error}</div>}
          <p className="muted small">Nimmt alle Kontakte mit E-Mail-Adresse auf, die noch nicht in dieser Liste stehen. Wer sich schon einmal abgemeldet hat, steht in der Liste und wird nie übernommen. Mit den Bedingungen lässt sich der Kreis eingrenzen.</p>
          <div className="field">
            <span>Beziehungsart des Kontakts</span>
            <RelationPicker value={bulkAudience.relationTypeIds} onChange={(ids) => setBulkAudience((a) => ({ ...a, relationTypeIds: ids }))} />
          </div>
          <fieldset className="mk-choice mk-choice--row">
            <legend>Art des Kontakts</legend>
            <label><input type="checkbox" checked={bulkAudience.kinds.includes('person')} onChange={() => toggleKind('person')} /> Personen</label>
            <label><input type="checkbox" checked={bulkAudience.kinds.includes('company')} onChange={() => toggleKind('company')} /> Firmen</label>
          </fieldset>
          <label className="field mk-narrow">
            <span>Postleitzahl beginnt mit</span>
            <input className="input" inputMode="numeric" value={bulkAudience.postalCodePrefix ?? ''} onChange={(e) => setBulkAudience((a) => ({ ...a, postalCodePrefix: e.target.value.replace(/\D/g, '').slice(0, 5) }))} maxLength={5} placeholder="Zum Beispiel 844" />
          </label>
          <p className={`mk-reach${bulkCount.data === 0 ? ' mk-reach--zero' : ''}`} role="status">{bulkCount.data === undefined ? 'Wird gezählt …' : <><strong>{bulkCount.data.toLocaleString('de-DE')}</strong> Kontakte würden aufgenommen.</>}</p>
          <fieldset className="mk-choice">
            <legend>Einwilligung</legend>
            <label><input type="radio" name="bulkmode" checked={bulkMode === 'invite'} onChange={() => setBulkMode('invite')} /> <span><strong>Bestätigung anfordern</strong><span className="muted small"> – jeder bekommt eine Mail mit Link und ist erst nach dem Klick angemeldet (Double-Opt-In)</span></span></label>
            <label><input type="radio" name="bulkmode" checked={bulkMode === 'confirmed'} onChange={() => setBulkMode('confirmed')} /> <span><strong>Einwilligung liegt für alle vor</strong><span className="muted small"> – alle gelten sofort als angemeldet, Sie halten einen gemeinsamen Nachweis fest</span></span></label>
          </fieldset>
          {bulkMode === 'confirmed' && (
            <label className="field">
              <span>Nachweis der Einwilligung</span>
              <input className="input" value={bulkNote} onChange={(e) => setBulkNote(e.target.value)} maxLength={500} placeholder="Zum Beispiel: Kundenliste mit Zustimmung zum Newsletter, Stand 01.10.2026" required />
            </label>
          )}
          <div className="alert alert--warn" role="note">
            {bulkMode === 'invite'
              ? 'Auch schon die Bitte um Bestätigung ist eine Werbe-Mail. Sie ist nur erlaubt, wenn es einen Anlass gibt, zum Beispiel eine bestehende Kundenbeziehung, bei der Sie die Adresse im Zusammenhang mit einem Kauf erhalten haben. Mails an Adressen ohne jeden Bezug (gekaufte oder gesammelte Listen) sind unzulässig.'
              : 'Wenn Sie hier „Einwilligung liegt vor“ wählen, müssen Sie die Einwilligung jedes einzelnen Kontakts belegen können. Eine Kundenbeziehung allein ersetzt sie nicht. Im Zweifel nehmen Sie „Bestätigung anfordern“.'}
          </div>
          <label className="mk-check"><input type="checkbox" checked={bulkAck} onChange={(e) => setBulkAck(e.target.checked)} /> <span>Ich habe den Hinweis gelesen und trage die Verantwortung für diese Aufnahme.</span></label>
          <div className="form-actions">
            <button type="submit" className="btn btn--primary" disabled={bulkAdd.isPending || !bulkAck || !bulkCount.data}>{bulkAdd.isPending ? 'Wird aufgenommen …' : bulkMode === 'invite' ? `${(bulkCount.data ?? 0).toLocaleString('de-DE')} Bestätigungen senden` : `${(bulkCount.data ?? 0).toLocaleString('de-DE')} Kontakte anmelden`}</button>
            <button type="button" className="btn btn--secondary" onClick={() => setBulk(false)}>Abbrechen</button>
          </div>
        </form>
      )}
      {adding && (
        <form className="card mk-form" onSubmit={submit} aria-label="Abonnent hinzufügen">
          <h2>Abonnent hinzufügen</h2>
          {error && <div className="alert alert--error" role="alert">{error}</div>}
          <div className="field">
            <span>Kontakt</span>
            <ContactPicker value={contact} onChange={setContact} label="Kontakt" hint="Der Kontakt braucht eine E-Mail-Adresse." />
          </div>
          <fieldset className="mk-choice">
            <legend>Einwilligung</legend>
            <label><input type="radio" name="mode" checked={mode === 'invite'} onChange={() => setMode('invite')} /> <span><strong>Bestätigung anfordern</strong><span className="muted small"> – der Kontakt bekommt eine Mail mit Link und ist erst danach angemeldet (empfohlen, Double-Opt-In)</span></span></label>
            <label><input type="radio" name="mode" checked={mode === 'confirmed'} onChange={() => setMode('confirmed')} /> <span><strong>Einwilligung liegt vor</strong><span className="muted small"> – Sie halten den Nachweis selbst fest, zum Beispiel eine Unterschrift oder ein Formular</span></span></label>
          </fieldset>
          {mode === 'confirmed' && (
            <label className="field">
              <span>Nachweis der Einwilligung</span>
              <input className="input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="Zum Beispiel: Formular am Weinfest 12.10.2026 unterschrieben" required />
            </label>
          )}
          <div className="form-actions">
            <button type="submit" className="btn btn--primary" disabled={add.isPending}>{add.isPending ? 'Wird gespeichert …' : mode === 'invite' ? 'Bestätigung senden' : 'Anmelden'}</button>
            <button type="button" className="btn btn--secondary" onClick={() => setAdding(false)}>Abbrechen</button>
          </div>
        </form>
      )}
      <FilterBar search={{ value: q, placeholder: 'Name oder E-Mail', onSubmit: (v) => update({ q: v }) }} fields={fields} values={{ status }} onChange={(k, v) => update({ [k]: v })} onReset={() => setParams(new URLSearchParams())} />
      {list.isError && <div className="alert alert--error" role="alert">{(list.error as Error).message}</div>}
      <div className="card table-card">
        <table className="table table--cards">
          <thead>
            <tr><th>Kontakt</th><th>Stand</th><th>Einwilligung</th><th>Herkunft</th><th /></tr>
          </thead>
          <tbody>
            {list.isLoading && <tr><td colSpan={6} className="muted">Wird geladen …</td></tr>}
            {list.data?.data.length === 0 && <tr><td colSpan={6} className="empty">{q || status ? 'Keine Abonnenten zu dieser Suche.' : 'Noch keine Abonnenten. Fügen Sie Kontakte hinzu, die Ihren Newsletter erhalten möchten.'}</td></tr>}
            {list.data?.data.map((s) => (
              <ExpandRow
                key={s.id}
                className={s.status === 'unsubscribed' ? 'inv-reversed' : ''}
                summary={<Summary title={<Link to={`/kontakte/${s.contactId}`} className="strong-link">{s.contactName}</Link>} aside={<SubscriptionBadge status={s.status} />} line2={s.email ?? 'Keine E-Mail-Adresse'} line3={s.status === 'unsubscribed' ? `Abgemeldet ${day(s.unsubscribedAt)}` : s.status === 'subscribed' ? `Seit ${day(s.consentedAt)}` : 'Wartet auf die Bestätigung'} />}
              >
                <td className="cell-desktop-title"><Link to={`/kontakte/${s.contactId}`} className="strong-link">{s.contactName}</Link><div className="muted small">{s.email}</div></td>
                <td data-label="Stand"><SubscriptionBadge status={s.status} />{s.status === 'unsubscribed' && s.unsubscribeReason && <div className="muted small">{s.unsubscribeReason}</div>}</td>
                <td data-label="Einwilligung">{s.status === 'subscribed' ? when(s.consentedAt) : s.status === 'pending' ? (s.confirmSentAt ? `Mail gesendet ${when(s.confirmSentAt)}` : 'Mail nicht gesendet') : `Abgemeldet ${when(s.unsubscribedAt)}`}{s.consentNote && <div className="muted small">{s.consentNote}</div>}</td>
                <td data-label="Herkunft">{SUBSCRIPTION_SOURCE_LABELS[s.source]}</td>
                <td className="doc-actions">
                  {can('marketing.write') && s.status === 'pending' && <button type="button" className="btn btn--secondary btn--sm" onClick={() => act.mutate({ id: s.id, action: 'resend' })}>Erneut senden</button>}
                  {can('marketing.write') && s.status !== 'unsubscribed' && <button type="button" className="btn btn--secondary btn--sm" onClick={() => window.confirm(`${s.contactName} vom Newsletter abmelden? Danach bekommt der Kontakt keine Kampagnen mehr.`) && act.mutate({ id: s.id, action: 'unsubscribe' })}>Abmelden</button>}
                  {can('marketing.delete') && s.status === 'pending' && <button type="button" className="btn btn--danger btn--sm" onClick={() => window.confirm('Diese offene Anmeldung löschen?') && act.mutate({ id: s.id, action: 'remove' })}>Löschen</button>}
                  {can('marketing.write') && s.status === 'unsubscribed' && <button type="button" className="btn btn--secondary btn--sm" onClick={() => { setContact({ id: s.contactId, name: s.contactName }); setAdding(true); setNotice(null); window.scrollTo({ top: 0 }); }}>Wieder anmelden</button>}
                </td>
              </ExpandRow>
            ))}
          </tbody>
        </table>
      </div>
      {list.data && list.data.meta.lastPage > 1 && (
        <div className="pager">
          <button type="button" className="btn btn--secondary btn--sm" disabled={page <= 1} onClick={() => update({ page: String(page - 1) })}>Zurück</button>
          <span className="muted">Seite {page} von {list.data.meta.lastPage}</span>
          <button type="button" className="btn btn--secondary btn--sm" disabled={page >= list.data.meta.lastPage} onClick={() => update({ page: String(page + 1) })}>Weiter</button>
        </div>
      )}
    </>
  );
}
